import React from 'react';
import {
  Button,
  Form,
  Spinner,
} from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';

import BaseModal from '@/components/BaseModal.js';
import VideoPreview from '@/components/media/VideoPreview.js';
import {
  JIANYING_TEMPLATE_RUNTIME_STATUS,
  getJianyingStatusTone,
  getJianyingTemplateStatusLabel,
} from '@/features/systemManagement/jianying/constants/jianyingTemplateStatus.js';
import JianyingAudioMaterialPanel from './components/panels/audio/JianyingAudioMaterialPanel.js';
import JianyingImageMaterialPanel from './components/panels/image/JianyingImageMaterialPanel.js';
import JianyingOrderPicker from './components/JianyingOrderPicker.js';
import JianyingTaskFooter from './components/JianyingTaskFooter.js';
import JianyingTextMaterialPanel from './components/panels/text/JianyingTextMaterialPanel.js';
import JianyingVideoMaterialPanel from './components/panels/video/JianyingVideoMaterialPanel.js';
import useJianyingCreateWorkspace, {
  getJianyingCreateErrorMessage,
  getJianyingTemplateRuntimeStatus,
} from './hooks/useJianyingCreateWorkspace.js';
import useJianyingJobSubmission from './hooks/useJianyingJobSubmission.js';
import '../../../../jianying/JianyingCommon.scss';
import '../../JianyingAutomation.scss';

const JianyingCreatePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get('client_id') || '';
  const workspace = useJianyingCreateWorkspace({ orderId, setSearchParams });
  const {
    activeMaterialType,
    audioFieldErrors,
    audioSections,
    audioSlots,
    automation,
    canUseJianying,
    chooseCopyPreset,
    chooseOrder,
    chooseTemplate,
    clientName,
    devicePickerExpanded,
    devicesLoading,
    devicesMessage,
    eligibleOrders,
    fieldErrors,
    imageFieldErrors,
    imageSections,
    loading,
    materialTypes,
    message,
    openDevicePicker,
    orderFilter,
    orderImages,
    orderPickerExpanded,
    orders,
    ordersLoading,
    ordersMessage,
    preferredDeviceId,
    preferredDevices,
    processAllSmartCrop,
    processSingleSmartCrop,
    registerFieldRef,
    registerImageFieldRef,
    selectedTemplate,
    selectedCopyPresetId,
    selectedTemplateId,
    setActiveMaterialType,
    setAudioSlotValue,
    setDevicePickerExpanded,
    setImageSlotValue,
    setMessage,
    setOrderFilter,
    setOrderPickerExpanded,
    setPreferredDeviceId,
    setTemplatePickerExpanded,
    setTextSlotValue,
    setVideoSlotValue,
    slotValues,
    smartCropStatus,
    templateAvailability,
    templatePickerExpanded,
    templates,
    textSections,
    textSlots,
    videoFieldErrors,
    videoSections,
    videoSlots,
  } = workspace;
  const {
    submitButtonLabel,
    submitJob,
    submitting,
  } = useJianyingJobSubmission({ navigate, orderId, workspace });
  const selectedTemplateAvailable = selectedTemplate
    && getJianyingTemplateRuntimeStatus(
      selectedTemplate,
      templateAvailability,
    ) === JIANYING_TEMPLATE_RUNTIME_STATUS.AVAILABLE;

  if (!canUseJianying) {
    return (
      <div className="jianying-workspace jianying-create">
        <div className="jianying-empty">当前账号无权使用剪映视频自动化。</div>
      </div>
    );
  }

  return (
    <main className="jianying-workspace jianying-create">
      <BaseModal
        show={orderPickerExpanded}
        onClose={() => setOrderPickerExpanded(false)}
        closable={Boolean(orderId) && !submitting}
        title="选择订单"
        className="jianying-selection-modal"
      >
        <JianyingOrderPicker
          eligibleOrders={eligibleOrders}
          filter={orderFilter}
          loading={ordersLoading || loading}
          message={ordersMessage}
          onChoose={chooseOrder}
          onFilterChange={setOrderFilter}
          orderCount={orders.length}
          orderId={orderId}
        />
      </BaseModal>

      {message ? <p className="jianying-notice jianying-create__message" role="alert">{message}</p> : null}
      {loading ? <div className="jianying-empty"><Spinner size="sm" /> 正在读取订单</div> : null}

      {!loading && orderId && automation && !automation.supported ? (
        <div className="jianying-empty">该订单的视频类型还没有可用的剪映模板。</div>
      ) : null}

      {!loading && templates.length > 0 ? (
        <div className="jianying-create__layout">
          <BaseModal
            show={templatePickerExpanded}
            onClose={() => setTemplatePickerExpanded(false)}
            closable={!submitting}
            title="选择模板"
            className="jianying-selection-modal"
          >
            <aside className="jianying-template-picker is-modal" aria-label="剪映模板">
                {templates.map((template) => {
                  const runtimeStatus = getJianyingTemplateRuntimeStatus(
                    template,
                    templateAvailability,
                  );
                  const isAvailable = runtimeStatus
                    === JIANYING_TEMPLATE_RUNTIME_STATUS.AVAILABLE;
                  const isSelected = String(selectedTemplateId) === String(template.id);
                  return (
                    <div
                      key={template.id}
                      className={[
                        'jianying-template-picker__option',
                        isSelected ? 'is-selected' : '',
                        isAvailable ? '' : 'is-unavailable',
                      ].filter(Boolean).join(' ')}
                    >
                      {template.template_preview ? (
                        <VideoPreview
                          ariaLabel={`播放 ${template.name} 模板视频`}
                          className="jianying-template-picker__preview"
                          overlay={(
                            <span className="jianying-template-picker__play" aria-hidden="true">
                              <i className="bi bi-play-fill" />
                            </span>
                          )}
                          src={template.template_preview}
                          title={template.name}
                        />
                      ) : (
                        <div className="jianying-template-picker__preview is-empty">
                          <i className="bi bi-camera-video" />
                        </div>
                      )}
                      <button
                        type="button"
                        className="jianying-template-picker__select"
                        onClick={() => chooseTemplate(template)}
                        disabled={
                          submitting
                          || runtimeStatus !== JIANYING_TEMPLATE_RUNTIME_STATUS.AVAILABLE
                        }
                      >
                        <strong>{template.name}</strong>
                        <span>{template.draft_name}</span>
                        <small>
                          {Array.isArray(template.slots)
                            ? template.slots.filter(
                              (slot) => slot?.replace_enabled !== false,
                            ).length
                            : 0} 个替换项
                        </small>
                        <small
                          className={[
                            'jianying-template-picker__runtime',
                            `is-${getJianyingStatusTone(template.status, runtimeStatus)}`,
                          ].join(' ')}
                        >
                          {runtimeStatus === JIANYING_TEMPLATE_RUNTIME_STATUS.CHECKING ? (
                            <Spinner animation="border" size="sm" aria-hidden="true" />
                          ) : null}
                          {getJianyingTemplateStatusLabel(template.status, runtimeStatus)}
                        </small>
                      </button>
                    </div>
                  );
                })}
            </aside>
          </BaseModal>

          <BaseModal
            show={devicePickerExpanded}
            onClose={() => setDevicePickerExpanded(false)}
            closable={!submitting}
            title="选择执行设备"
            className="jianying-create-device-modal"
            footer={(
              <>
                <Button
                  variant="outline-secondary"
                  disabled={submitting}
                  onClick={() => setDevicePickerExpanded(false)}
                >
                  取消
                </Button>
                <Button
                  className="jianying-primary-action"
                  disabled={submitting || devicesLoading}
                  onClick={() => {
                    setDevicePickerExpanded(false);
                    submitJob();
                  }}
                >
                  {submitButtonLabel}
                </Button>
              </>
            )}
          >
            <Form.Group controlId="jianying-create-preferred-device">
              <Form.Label>优先设备（可选）</Form.Label>
              <Form.Select
                value={preferredDeviceId}
                disabled={submitting || devicesLoading}
                onChange={(event) => setPreferredDeviceId(event.target.value)}
              >
                <option value="">自动选择</option>
                {preferredDevices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.name}（优先级 {device.priority}）
                  </option>
                ))}
              </Form.Select>
              <Form.Text>
                {devicesLoading
                  ? '正在读取可用设备…'
                  : devicesMessage
                    || (preferredDevices.length > 0
                      ? '只显示在线、未达到积压上限且拥有当前模板的设备。'
                      : '当前没有可手动选择的设备，将由系统自动校验。')}
              </Form.Text>
            </Form.Group>
          </BaseModal>

          <section className="jianying-replacement-editor">
            {!selectedTemplate ? <div className="jianying-empty">请选择一个剪映模板。</div> : null}
            {selectedTemplate
              && textSlots.length === 0
              && imageSections.length === 0
              && audioSlots.length === 0
              && videoSlots.length === 0 ? (
              <div className="jianying-empty">该模板没有需要替换的素材，可直接创建任务。</div>
            ) : null}

            <div hidden={activeMaterialType !== 'text'}>
              <JianyingTextMaterialPanel
                copyPresets={selectedTemplate?.copy_presets || []}
                disabled={submitting}
                fieldErrors={fieldErrors}
                onChange={setTextSlotValue}
                onPresetChange={chooseCopyPreset}
                registerFieldRef={registerFieldRef}
                sections={textSections}
                selectedCopyPresetId={selectedCopyPresetId}
                slots={textSlots}
                values={slotValues}
              />
            </div>

            <JianyingImageMaterialPanel
              disabled={submitting}
              errors={imageFieldErrors}
              hidden={activeMaterialType !== 'image'}
              images={orderImages}
              onChange={setImageSlotValue}
              onProcessAll={processAllSmartCrop}
              onSmartCrop={processSingleSmartCrop}
              registerFieldRef={registerImageFieldRef}
              sections={imageSections}
              smartCropStatus={smartCropStatus}
              values={slotValues}
            />
            <div hidden={activeMaterialType !== 'audio'}>
              <JianyingAudioMaterialPanel
                disabled={submitting}
                errors={audioFieldErrors}
                onChange={setAudioSlotValue}
                onError={(error) => setMessage(getJianyingCreateErrorMessage(error, '读取音频失败'))}
                sections={audioSections}
                slots={audioSlots}
                values={slotValues}
              />
            </div>

            <div hidden={activeMaterialType !== 'video'}>
              <JianyingVideoMaterialPanel
                disabled={submitting}
                errors={videoFieldErrors}
                onChange={setVideoSlotValue}
                onError={(error) => setMessage(getJianyingCreateErrorMessage(error, '读取视频失败'))}
                sections={videoSections}
                slots={videoSlots}
                values={slotValues}
              />
            </div>

          </section>
        </div>
      ) : null}

      <JianyingTaskFooter
        activeMaterialType={activeMaterialType}
        clientName={clientName}
        loading={loading}
        materialTypes={materialTypes}
        onChangeMaterialType={setActiveMaterialType}
        onChangeOrder={() => setOrderPickerExpanded(true)}
        onChangeTemplate={() => setTemplatePickerExpanded(true)}
        onCreate={openDevicePicker}
        orderId={orderId}
        selectedTemplate={selectedTemplate}
        selectedTemplateAvailable={selectedTemplateAvailable}
        submitButtonLabel={submitButtonLabel}
        submitting={submitting}
      />
    </main>
  );
};

export default JianyingCreatePage;
